import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Clock, Calendar as CalendarIcon } from "lucide-react";
import { DateSelectionProps } from "@/types/dialog-props";
import { useTariffsUnified as useTariffs } from '@/domains/tariffs/hooks/useTariffsUnified';

const DateSelectionSectionComponent: React.FC<DateSelectionProps> = ({
  tariffId,
  startDate,
  endDate,
  onTariffChange,
  onStartDateChange,
  onEndDateChange,
  isDisabled = false
}: DateSelectionProps): JSX.Element => {
  const { tariffs, isLoading: isLoadingTariffs } = useTariffs();
  // Render diagnostic
  // eslint-disable-next-line no-console
  console.log('[DateSelectionSection] render');
  
  // Minimal event shape for Radix outside events
  type OutsideEventLike = {
    preventDefault: () => void;
    target?: EventTarget | null;
    originalEvent?: { target?: EventTarget | null };
  };

  // Labeled wrappers to see which Radix outside handler fired first
  const withSource = (source: 'interact' | 'pointerDown' | 'focus') => (e: OutsideEventLike) => {
    try {
      // eslint-disable-next-line no-console
      console.log('[DateSelectionSection] outside-handler', source);
    } catch { /* noop */ }
    keepOpenOnCalendarControls(e);
  };

  const formatDisplay = (d?: Date | null): string => {
    if (!d) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = String(d.getFullYear());
    return `${dd}.${mm}.${yyyy}`;
  };

  const getEventTarget = (e: OutsideEventLike): HTMLElement | null => {
    const t = (e?.target ?? e?.originalEvent?.target) as HTMLElement | null | undefined;
    return t ?? null;
  };

  const isCalendarRelatedTarget = (el: HTMLElement | null | undefined): boolean => {
    if (!el) return false;
    return Boolean(
      el.closest('.calendar-container') ||
      el.closest('[data-year-month-panel]') ||
      el.closest('[role="listbox"]')
    );
  };

  const keepOpenOnCalendarControls = (e: OutsideEventLike) => {
    const target = getEventTarget(e);
    if (!target) return;
    // Temporary diagnostics to trace outside-event sources causing flicker
    try {
      const pathInfo = (target as HTMLElement).outerHTML?.slice(0, 120) || String(target);
      // eslint-disable-next-line no-console
      console.log('[DateSelectionSection] onOutsideEvent', {
        targetSnippet: pathInfo,
        inCalendar: Boolean(target.closest('.calendar-container')),
        inYearMonthPanel: Boolean(target.closest('[data-year-month-panel]')),
        inListbox: Boolean(target.closest('[role="listbox"]')),
      });
    } catch { /* noop */ }
    // Keep open for any interactions inside the calendar content
    if (target.closest('.calendar-container')) {
      e.preventDefault();
      return;
    }
    // Keep popover open when interacting with custom year/month panel inside Calendar
    if (target.closest('[data-year-month-panel]')) {
      e.preventDefault();
      return;
    }
    // Keep popover open when interacting with Radix Select listboxes (month/year dropdowns)
    if (target.closest('[role="listbox"]')) {
      e.preventDefault();
      return;
    }
  };

  const handleDateTimeChange = (date: Date | undefined, isStart: boolean, field: 'date' | 'hour' | 'minute', value?: string) => {
    if (!date && field === 'date') return;
    const currentDate = isStart ? startDate : endDate;
    let newDate = date || currentDate || new Date();
    if (field === 'hour' && value) {
      newDate = new Date(newDate);
      newDate.setHours(parseInt(value));
    } else if (field === 'minute' && value) {
      newDate = new Date(newDate);
      newDate.setMinutes(parseInt(value));
    }
    if (isStart) {
      onStartDateChange(newDate);
    } else {
      onEndDateChange(newDate);
    }
  };

  const getHoursOptions = () => {
    return Array.from({ length: 24 }, (_, i) => ({
      value: i.toString().padStart(2, '0'),
      label: i.toString().padStart(2, '0')
    }));
  };

  const getMinutesOptions = () => {
    return Array.from({ length: 12 }, (_, i) => ({
      value: (i * 5).toString().padStart(2, '0'),
      label: (i * 5).toString().padStart(2, '0')
    }));
  };

  return (
    <div className="bg-muted/50 rounded-lg border p-4">
      <h3 className="text-lg font-medium mb-4">Даты и тариф</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Тариф</Label>
          <Select value={tariffId && tariffId.trim() ? tariffId : undefined} onValueChange={onTariffChange} disabled={isDisabled || isLoadingTariffs}>
            <SelectTrigger>
              <SelectValue placeholder={isLoadingTariffs ? "Загрузка..." : "Выберите тариф"} />
            </SelectTrigger>
            <SelectContent>
              {tariffs?.map(tariff => (
                <SelectItem key={tariff.id} value={tariff.id}>
                  {tariff.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Дата начала</Label>
          <div className="space-y-2">
            <Popover modal={false} onOpenChange={(open) => console.log('[DateSelectionSection] popover(start) open', open)}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="w-full inline-flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm hover:bg-slate-50 transition-colors"
                  disabled={isDisabled}
                  onMouseEnter={() => console.log('[DateSelectionSection] trigger(start) mouseenter')}
                  onMouseLeave={() => console.log('[DateSelectionSection] trigger(start) mouseleave')}
                  onClick={() => console.log('[DateSelectionSection] trigger(start) click')}
                >
                  <span className="inline-flex items-center gap-2 text-slate-600">
                    <CalendarIcon className="h-4 w-4" />
                    {startDate ? formatDisplay(startDate) : 'Выберите дату'}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent
                onOpenAutoFocus={(e) => e.preventDefault()}
                onCloseAutoFocus={(e) => e.preventDefault()}
                onPointerDownCapture={(e) => {
                  const t = e.target as HTMLElement | null;
                  if (!isCalendarRelatedTarget(t)) e.stopPropagation();
                }}
                onFocusCapture={(e) => {
                  const t = e.target as HTMLElement | null;
                  if (!isCalendarRelatedTarget(t)) e.stopPropagation();
                }}
                onMouseDownCapture={(e) => {
                  const t = e.target as HTMLElement | null;
                  if (!isCalendarRelatedTarget(t)) e.stopPropagation();
                }}
                onClickCapture={(e) => {
                  const t = e.target as HTMLElement | null;
                  if (!isCalendarRelatedTarget(t)) e.stopPropagation();
                }}
                onMouseEnter={() => console.log('[DateSelectionSection] content(start) mouseenter')}
                onMouseLeave={() => console.log('[DateSelectionSection] content(start) mouseleave')}
                onInteractOutside={withSource('interact')}
                onPointerDownOutside={withSource('pointerDown')}
                onFocusOutside={withSource('focus')}
                className="w-auto p-0 max-w-[320px] overflow-visible"
                align="start"
                sideOffset={6}
                side="bottom"
                avoidCollisions={false}
                forceMount
              >
                <Calendar mode="single" selected={startDate} onSelect={date => handleDateTimeChange(date || undefined, true, 'date')} />
              </PopoverContent>
            </Popover>
            <div className="flex gap-2">
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground flex items-center mb-1"><Clock className="mr-1 h-3 w-3" />чч</Label>
                <Select value={startDate ? startDate.getHours().toString().padStart(2, '0') : '09'} onValueChange={value => handleDateTimeChange(undefined, true, 'hour', value)} disabled={isDisabled}>
                  <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {getHoursOptions().map(option => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground flex items-center mb-1"><Clock className="mr-1 h-3 w-3" />мм</Label>
                <Select value={startDate ? startDate.getMinutes().toString().padStart(2, '0') : '00'} onValueChange={value => handleDateTimeChange(undefined, true, 'minute', value)} disabled={isDisabled}>
                  <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {getMinutesOptions().map(option => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Дата окончания</Label>
          <div className="space-y-2">
            <Popover modal={false} onOpenChange={(open) => console.log('[DateSelectionSection] popover(end) open', open)}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="w-full inline-flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm hover:bg-slate-50 transition-colors"
                  disabled={isDisabled}
                  onMouseEnter={() => console.log('[DateSelectionSection] trigger(end) mouseenter')}
                  onMouseLeave={() => console.log('[DateSelectionSection] trigger(end) mouseleave')}
                  onClick={() => console.log('[DateSelectionSection] trigger(end) click')}
                >
                  <span className="inline-flex items-center gap-2 text-slate-600">
                    <CalendarIcon className="h-4 w-4" />
                    {endDate ? formatDisplay(endDate) : 'Выберите дату'}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent
                onOpenAutoFocus={(e) => e.preventDefault()}
                onCloseAutoFocus={(e) => e.preventDefault()}
                onPointerDownCapture={(e) => {
                  const t = e.target as HTMLElement | null;
                  if (!isCalendarRelatedTarget(t)) e.stopPropagation();
                }}
                onFocusCapture={(e) => {
                  const t = e.target as HTMLElement | null;
                  if (!isCalendarRelatedTarget(t)) e.stopPropagation();
                }}
                onMouseDownCapture={(e) => {
                  const t = e.target as HTMLElement | null;
                  if (!isCalendarRelatedTarget(t)) e.stopPropagation();
                }}
                onClickCapture={(e) => {
                  const t = e.target as HTMLElement | null;
                  if (!isCalendarRelatedTarget(t)) e.stopPropagation();
                }}
                onMouseEnter={() => console.log('[DateSelectionSection] content(end) mouseenter')}
                onMouseLeave={() => console.log('[DateSelectionSection] content(end) mouseleave')}
                onInteractOutside={withSource('interact')}
                onPointerDownOutside={withSource('pointerDown')}
                onFocusOutside={withSource('focus')}
                className="w-auto p-0 max-w-[320px] overflow-visible"
                align="start"
                sideOffset={6}
                side="bottom"
                avoidCollisions={false}
                forceMount
              >
                <Calendar mode="single" selected={endDate} onSelect={date => handleDateTimeChange(date || undefined, false, 'date')} />
              </PopoverContent>
            </Popover>
            <div className="flex gap-2">
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground flex items-center mb-1"><Clock className="mr-1 h-3 w-3" />чч</Label>
                <Select value={endDate ? endDate.getHours().toString().padStart(2, '0') : '18'} onValueChange={value => handleDateTimeChange(undefined, false, 'hour', value)} disabled={isDisabled}>
                  <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {getHoursOptions().map(option => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground flex items-center mb-1"><Clock className="mr-1 h-3 w-3" />мм</Label>
                <Select value={endDate ? endDate.getMinutes().toString().padStart(2, '0') : '00'} onValueChange={value => handleDateTimeChange(undefined, false, 'minute', value)} disabled={isDisabled}>
                  <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {getMinutesOptions().map(option => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const areEqual = (prev: DateSelectionProps, next: DateSelectionProps): boolean => {
  const prevStart = prev.startDate ? prev.startDate.getTime() : null;
  const nextStart = next.startDate ? next.startDate.getTime() : null;
  const prevEnd = prev.endDate ? prev.endDate.getTime() : null;
  const nextEnd = next.endDate ? next.endDate.getTime() : null;
  return (
    prev.tariffId === next.tariffId &&
    prevStart === nextStart &&
    prevEnd === nextEnd &&
    prev.isDisabled === next.isDisabled
  );
};

export const DateSelectionSection = React.memo(DateSelectionSectionComponent, areEqual);
DateSelectionSection.displayName = 'DateSelectionSection';